import { useState } from "react";
import { submitContact } from "@/lib/api";
import { toast } from "sonner";
import { ArrowRight } from "lucide-react";
import SectionHeading from "@/components/SectionHeading";
import { useSiteContent } from "@/contexts/SiteContent";

const EMPTY = {
  full_name: "",
  email: "",
  phone: "",
  budget_range: "",
  interest: "",
  message: "",
};

const BUDGETS = ["Less than AED 10M", "AED 10M – 25M", "AED 25M – 75M", "AED 75M – 150M", "AED 150M+"];
const INTERESTS = ["Palm Jumeirah", "Downtown / Burj Khalifa", "Emirates Hills", "Jumeirah Bay Island", "Dubai Hills", "Dubai City Walk", "Bluewaters Island", "Jumeirah Um Suqeim", "Off-Market Mandate"];

export default function Contact() {
  const content = useSiteContent();
  const cfg = content?.contact || {};
  const [form, setForm] = useState(EMPTY);
  const [busy, setBusy] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.full_name || !form.email || !form.message) {
      toast.error("Name, email and message are required.");
      return;
    }
    setBusy(true);
    try {
      await submitContact(form);
      toast.success("Received. A private advisor will reach you within 24 hours.");
      setForm(EMPTY);
    } catch {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <section
      id="contact"
      data-testid="contact-section"
      className="py-24 lg:py-36 bg-[#f5f0e8] border-t border-[#1c1c1c]/10"
    >
      <div className="max-w-[1400px] mx-auto px-6 lg:px-12">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20">
          <div className="lg:col-span-5">
            <SectionHeading
              title={<>Request a private<br /><em className="not-italic text-[#3d2b1f]">introduction</em>.</>}
              subtitle={cfg.subtitle || "Begin the Conversation"}
            />
            <p className="mt-8 text-[#3d2b1f] text-base max-w-md leading-relaxed">
              Share a few details and one of our directors will arrange a confidential
              conversation — typically within twenty-four hours.
            </p>

            <div className="mt-12 space-y-6">
              <div>
                <div className="underline-label mb-2 text-[#3d2b1f]/70">Dubai Office</div>
                <div className="font-display text-xl text-[#1c1c1c]">{cfg.office_line_1 || "B2B Office Tower"}</div>
                <div className="text-[#3d2b1f] text-sm mt-1">{cfg.office_line_2 || "Dubai Business Bay · Office 905"}</div>
              </div>
              <div>
                <div className="underline-label mb-2 text-[#3d2b1f]/70">Private Line</div>
                <a href={`tel:${cfg.phone_raw || "+971521151243"}`} className="font-display text-xl text-[#1c1c1c] hover:text-[#c5a059] transition-colors">
                  {cfg.phone_display || "+971 52 115 1243"}
                </a>
              </div>
              <div>
                <div className="underline-label mb-2 text-[#3d2b1f]/70">Concierge</div>
                <a href={`mailto:${cfg.email || "concierge@azefac.ae"}`} className="font-display text-xl text-[#1c1c1c] hover:text-[#c5a059] transition-colors">
                  {cfg.email || "concierge@azefac.ae"}
                </a>
              </div>
            </div>
          </div>

          <form
            onSubmit={submit}
            data-testid="contact-form"
            className="lg:col-span-7 grid grid-cols-1 md:grid-cols-2 gap-x-10 gap-y-8"
          >
            <div className="md:col-span-1">
              <label className="underline-label mb-3 block">Full Name</label>
              <input
                data-testid="contact-input-full_name"
                className="editorial-input"
                value={form.full_name}
                onChange={set("full_name")}
                required
              />
            </div>
            <div className="md:col-span-1">
              <label className="underline-label mb-3 block">Email</label>
              <input
                data-testid="contact-input-email"
                type="email"
                className="editorial-input"
                value={form.email}
                onChange={set("email")}
                required
              />
            </div>
            <div className="md:col-span-1">
              <label className="underline-label mb-3 block">Phone</label>
              <input
                data-testid="contact-input-phone"
                className="editorial-input"
                value={form.phone}
                onChange={set("phone")}
              />
            </div>
            <div className="md:col-span-1">
              <label className="underline-label mb-3 block">Budget Range</label>
              <select
                data-testid="contact-select-budget_range"
                className="editorial-input bg-transparent appearance-none cursor-pointer"
                value={form.budget_range}
                onChange={set("budget_range")}
              >
                <option value="" className="bg-[#f5f0e8]">Select</option>
                {BUDGETS.map((b) => (
                  <option key={b} value={b} className="bg-[#f5f0e8]">{b}</option>
                ))}
              </select>
            </div>
            <div className="md:col-span-2">
              <label className="underline-label mb-3 block">Area of Interest</label>
              <select
                data-testid="contact-select-interest"
                className="editorial-input bg-transparent appearance-none cursor-pointer"
                value={form.interest}
                onChange={set("interest")}
              >
                <option value="" className="bg-[#f5f0e8]">Select a neighbourhood or mandate</option>
                {INTERESTS.map((i) => (
                  <option key={i} value={i} className="bg-[#f5f0e8]">{i}</option>
                ))}
              </select>
            </div>
            <div className="md:col-span-2">
              <label className="underline-label mb-3 block">Message</label>
              <textarea
                data-testid="contact-input-message"
                className="editorial-input resize-none"
                rows={4}
                value={form.message}
                onChange={set("message")}
                required
              />
            </div>
            <div className="md:col-span-2 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-6 pt-4">
              <p className="text-[11px] text-[#3d2b1f]/70 max-w-md leading-relaxed">
                Your details are handled with strict confidentiality and synced to our private
                advisory CRM.
              </p>
              <button
                data-testid="contact-submit"
                type="submit"
                disabled={busy}
                className="inline-flex items-center gap-4 px-7 py-4 bg-[#c5a059] text-[#1c1c1c] uppercase text-[11px] tracking-[0.32em] hover:bg-[#1c1c1c] hover:text-[#f5f0e8] transition-colors duration-500 disabled:opacity-60"
              >
                {busy ? "Sending…" : "Request Introduction"}
                <ArrowRight size={14} />
              </button>
            </div>
          </form>
        </div>
      </div>
    </section>
  );
}
